import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import type { ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm/cancel buttons are disabled and the pending label is shown. */
  isPending?: boolean;
  /** Whether the action is destructive (red confirm button). Defaults to true. */
  destructive?: boolean;
  onConfirm: () => void;
}

/**
 * Generic "Are you sure?" confirmation dialog for destructive actions.
 *
 * Built on the shared AlertDialog primitive so behaviour and styling stay
 * consistent across the app. Controlled via `open` / `onOpenChange`; the
 * consumer owns the pending state and closes the dialog after the action
 * settles (or lets a rejection re-enable the buttons).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  pendingLabel = 'Deleting…',
  cancelLabel = 'Cancel',
  isPending = false,
  destructive = true,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description ? <AlertDialogDescription>{description}</AlertDialogDescription> : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isPending}
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : undefined}
          >
            {isPending ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
